/**
 * Create Custom View Dialog - Dynamic Query Builder
 * Zendesk-style condition builder with AND/OR logic
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Mail, FileText, Users, AlertTriangle, CheckCircle, Clock, Package,
  Zap, Archive, AlertCircle, Truck, DollarSign, UserCheck, Building,
  Save, Filter, Eye, Loader2, Plus, Minus, GripVertical
} from 'lucide-react';
import usersService from '@/services/users.service';
import organizationsService from '@/services/organizations.service';
import customViewsService from '@/services/customViews.service';
import ticketsService from '@/services/tickets.service';
import { useCreateCustomView, useUpdateCustomView } from '@/hooks/useCustomViews';
import { useTags } from '@/hooks/useTags';
import useAuthStore from '@/stores/authStore';
import type { FilterCondition, CustomViewFilterConfig } from '@/services/customViews.service';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { dfLocale } from '@/lib/dateFnsLocale';
import { useTranslation } from 'react-i18next';

// Icon options
const iconOptions = [
  { value: 'Mail', labelKey: 'views:f.icoMail', label: 'Mail', icon: Mail },
  { value: 'FileText', labelKey: 'views:f.icoDoc', label: 'Dokument', icon: FileText },
  { value: 'Users', labelKey: 'common:field.users', label: 'Benutzer', icon: Users },
  { value: 'AlertTriangle', labelKey: 'views:f.icoWarning', label: 'Warnung', icon: AlertTriangle },
  { value: 'CheckCircle', labelKey: 'views:f.sDone', label: 'Erledigt', icon: CheckCircle },
  { value: 'Clock', labelKey: 'views:f.icoClock', label: 'Uhr', icon: Clock },
  { value: 'Package', labelKey: 'views:f.icoPackage', label: 'Paket', icon: Package },
  { value: 'Zap', labelKey: 'views:f.icoZap', label: 'Blitz', icon: Zap },
  { value: 'Archive', labelKey: 'views:f.icoArchive', label: 'Archiv', icon: Archive },
  { value: 'AlertCircle', labelKey: 'views:f.icoInfo', label: 'Info', icon: AlertCircle },
  { value: 'Truck', labelKey: 'views:f.icoTruck', label: 'Lieferung', icon: Truck },
  { value: 'DollarSign', labelKey: 'views:f.icoPayment', label: 'Zahlung', icon: DollarSign },
  { value: 'UserCheck', labelKey: 'views:f.sAssigned', label: 'Zugewiesen', icon: UserCheck },
  { value: 'Building', labelKey: 'common:field.organization', label: 'Organisation', icon: Building },
];

const iconMap: Record<string, any> = {
  Mail, FileText, Users, AlertTriangle, CheckCircle, Clock, Package,
  Zap, Archive, AlertCircle, Truck, DollarSign, UserCheck, Building
};

// Field definitions with operators and value types
const fieldDefinitions = [
  {
    value: 'status',
    labelKey: 'common:field.status', label: 'Status',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'select',
    options: [
      { value: 'new', labelKey: 'domain:status.new', label: 'Neu' },
      { value: 'open', labelKey: 'domain:status.open', label: 'Offen' },
      { value: 'in_progress', labelKey: 'domain:status.in_progress', label: 'In Bearbeitung' },
      { value: 'pending', labelKey: 'domain:status.pending', label: 'Wartend' },
      { value: 'solved', labelKey: 'domain:status.solved', label: 'Gelöst' },
      { value: 'closed', labelKey: 'domain:status.closed', label: 'Geschlossen' },
    ]
  },
  {
    value: 'priority',
    labelKey: 'ticketList:column.priority', label: 'Priorität',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
      { value: 'greater_than', labelKey: 'views:op.gt', label: 'größer als' },
      { value: 'less_than', labelKey: 'views:op.lt', label: 'kleiner als' },
    ],
    valueType: 'select',
    options: [
      { value: 'low', labelKey: 'domain:priority.low', label: 'Niedrig' },
      { value: 'normal', labelKey: 'domain:priority.normal', label: 'Normal' },
      { value: 'high', labelKey: 'domain:priority.high', label: 'Hoch' },
      { value: 'urgent', labelKey: 'domain:priority.urgent', label: 'Dringend' },
    ]
  },
  {
    value: 'subject',
    labelKey: 'common:field.subject', label: 'Betreff',
    operators: [
      { value: 'contains', labelKey: 'views:op.contains', label: 'enthält' },
      { value: 'not_contains', labelKey: 'views:op.notContains', label: 'enthält nicht' },
      { value: 'starts_with', labelKey: 'views:op.startsWith', label: 'beginnt mit' },
      { value: 'ends_with', labelKey: 'views:op.endsWith', label: 'endet mit' },
    ],
    valueType: 'text'
  },
  {
    value: 'assignee_id',
    labelKey: 'views:f.fOwner', label: 'Besitzer',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'agent',
    options: [] // Will be filled dynamically
  },
  {
    value: 'watcher_id',
    labelKey: 'common:field.watcher', label: 'Beobachter',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'watcher',
    options: [] // Will be filled dynamically
  },
  {
    value: 'tags',
    labelKey: 'views:f.fTags', label: 'Tags',
    operators: [
      { value: 'includes', labelKey: 'views:op.contains', label: 'enthält' },
      { value: 'not_includes', labelKey: 'views:op.notContains', label: 'enthält nicht' },
    ],
    valueType: 'tag',
    options: [] // Will be filled dynamically
  },
  {
    value: 'organization_id',
    labelKey: 'common:field.organization', label: 'Organisation',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'organization',
    options: [] // Will be filled dynamically
  },
  {
    value: 'created_at',
    labelKey: 'views:f.fCreatedAt', label: 'Erstellt am',
    operators: [
      { value: 'less_than', labelKey: 'views:op.lessThanAgo', label: 'vor weniger als' },
      { value: 'greater_than', labelKey: 'views:op.moreThanAgo', label: 'vor mehr als' },
      { value: 'is', labelKey: 'views:op.exactly', label: 'ist genau' },
    ],
    valueType: 'relative_date'
  },
  {
    value: 'updated_at',
    labelKey: 'views:f.fLastContact', label: 'Letzter Kontakt',
    operators: [
      { value: 'less_than', labelKey: 'views:op.lessThanAgo', label: 'vor weniger als' },
      { value: 'greater_than', labelKey: 'views:op.moreThanAgo', label: 'vor mehr als' },
      { value: 'is', labelKey: 'views:op.exactly', label: 'ist genau' },
    ],
    valueType: 'relative_date'
  },
  {
    value: 'source_type',
    labelKey: 'views:f.fChannel', label: 'Kanal',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'select',
    options: [
      { value: 'incoming', labelKey: 'views:f.sIncoming', label: 'Eingehend' },
      { value: 'outgoing', labelKey: 'views:f.sOutgoing', label: 'Ausgehend' },
      { value: 'internal', labelKey: 'views:f.sInternal', label: 'Intern' },
    ]
  },
  {
    value: 'sla_status',
    labelKey: 'views:f.fSlaStatus', label: 'SLA-Status',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'select',
    options: [
      { value: 'active', labelKey: 'common:state.active', label: 'Aktiv' },
      { value: 'breached', labelKey: 'views:f.sBreached', label: 'Überschritten' },
      { value: 'paused', labelKey: 'views:f.sPaused', label: 'Pausiert' },
    ]
  },
  {
    value: 'ticket_type',
    labelKey: 'views:f.fTicketType', label: 'Ticket-Typ',
    operators: [
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
      { value: 'is_not', labelKey: 'views:op.notEquals', label: 'ist nicht' },
    ],
    valueType: 'select',
    options: [
      { value: 'normal', labelKey: 'domain:priority.normal', label: 'Normal' },
      { value: 'external', labelKey: 'views:f.sExternal', label: 'Extern' },
    ]
  },
  {
    value: 'from_email',
    labelKey: 'views:f.fSenderEmail', label: 'Absender E-Mail',
    operators: [
      { value: 'contains', labelKey: 'views:op.contains', label: 'enthält' },
      { value: 'not_contains', labelKey: 'views:op.notContains', label: 'enthält nicht' },
      { value: 'is', labelKey: 'views:op.is', label: 'ist' },
    ],
    valueType: 'text'
  },
  {
    value: 'email_content',
    labelKey: 'views:f.fEmailBody', label: 'E-Mail Inhalt',
    operators: [
      { value: 'contains', labelKey: 'views:op.contains', label: 'enthält' },
      { value: 'not_contains', labelKey: 'views:op.notContains', label: 'enthält nicht' },
    ],
    valueType: 'text'
  },
];

interface Condition {
  id: string;
  field: string;
  operator: string;
  value: any;
}

interface ConditionGroup {
  id: string;
  name: string;
  conditions: Condition[];
}

interface EditViewData {
  id: string;
  name: string;
  iconName: string;
  filterConfig: CustomViewFilterConfig;
  sortConfig?: { sortBy: string; sortOrder: 'asc' | 'desc' };
}

interface CreateCustomViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  editView?: EditViewData | null;
}

export default function CreateCustomViewDialog({
  open,
  onOpenChange,
  onSuccess,
  editView = null
}: CreateCustomViewDialogProps) {
  const { t } = useTranslation();
  const createMutation = useCreateCustomView();
  const updateMutation = useUpdateCustomView();
  const isEditMode = !!editView;
  const { user } = useAuthStore();

  // Form state
  const [name, setName] = useState('');
  const [iconName, setIconName] = useState('Mail');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showPreview, setShowPreview] = useState(true);
  const [groupLogicType, setGroupLogicType] = useState<'AND' | 'OR'>('AND');

  // Max groups limit
  const MAX_GROUPS = 5;

  // Condition groups - each group has internal AND logic
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([
    {
      id: '1',
      name: 'Bedingungsgruppe 1',
      conditions: [{ id: '1-1', field: 'status', operator: 'is', value: '' }]
    }
  ]);

  // Populate form when editing
  useEffect(() => {
    if (editView && open) {
      setName(editView.name);
      setIconName(editView.iconName || 'Mail');
      if (editView.sortConfig) {
        setSortBy(editView.sortConfig.sortBy || 'created_at');
        setSortOrder(editView.sortConfig.sortOrder || 'desc');
      }
      // Convert filter config to condition groups
      if (editView.filterConfig?.filters && editView.filterConfig.filters.length > 0) {
        // Check if filterConfig has groups structure
        const filterConfig = editView.filterConfig as any;
        if (filterConfig.groups && Array.isArray(filterConfig.groups)) {
          // New grouped structure
          setGroupLogicType(filterConfig.groupLogic || 'AND');
          const newGroups: ConditionGroup[] = filterConfig.groups.map((group: any, groupIndex: number) => {
            const conditions: Condition[] = (group.conditions || []).map((filter: any, index: number) => {
              let operator = filter.operator;
              let value = filter.value;

              if (operator === 'in' && Array.isArray(value) && value.length === 1) {
                operator = 'is';
                value = value[0];
              } else if (operator === 'not_in' && Array.isArray(value) && value.length === 1) {
                operator = 'is_not';
                value = value[0];
              }

              return {
                id: `edit-${groupIndex}-${index}-${Date.now()}`,
                field: filter.field,
                operator,
                value
              };
            });
            return {
              id: `group-${groupIndex}-${Date.now()}`,
              name: `Bedingungsgruppe ${groupIndex + 1}`,
              conditions: conditions.length > 0 ? conditions : [{ id: `edit-${groupIndex}-0-${Date.now()}`, field: 'status', operator: 'is', value: '' }]
            };
          });
          setConditionGroups(newGroups.length > 0 ? newGroups : [{
            id: '1',
            name: 'Bedingungsgruppe 1',
            conditions: [{ id: '1-1', field: 'status', operator: 'is', value: '' }]
          }]);
        } else {
          // Legacy flat structure - convert to single group
          const newConditions: Condition[] = editView.filterConfig.filters.map((filter, index) => {
            let operator = filter.operator;
            let value = filter.value;

            if (operator === 'in' && Array.isArray(value) && value.length === 1) {
              operator = 'is';
              value = value[0];
            } else if (operator === 'not_in' && Array.isArray(value) && value.length === 1) {
              operator = 'is_not';
              value = value[0];
            }

            return {
              id: `edit-${index}-${Date.now()}`,
              field: filter.field,
              operator,
              value
            };
          });
          setConditionGroups([{
            id: '1',
            name: 'Bedingungsgruppe 1',
            conditions: newConditions
          }]);
        }
      }
    }
  }, [editView, open]);

  // Fetch data
  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => usersService.getAgents(true, 'active'),
  });

  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsService.getOrganizations(),
  });

  // Filter organizations based on user's access
  const allowedOrganizations = useMemo(() => {
    const allOrgs = orgsData?.data || [];
    const userOrgIds = (user as any)?.organizationIds || [];

    // If user has no specific org restrictions, show all (for admins without restrictions)
    if (userOrgIds.length === 0) {
      return allOrgs;
    }

    // Filter to only show organizations the user has access to
    return allOrgs.filter((org: any) => userOrgIds.includes(org.id));
  }, [orgsData?.data, user]);

  const { data: tagsData } = useTags();

  // Add condition group
  const addConditionGroup = () => {
    if (conditionGroups.length >= MAX_GROUPS) return;
    const newGroupNumber = conditionGroups.length + 1;
    setConditionGroups([
      ...conditionGroups,
      {
        id: Date.now().toString(),
        name: `Bedingungsgruppe ${newGroupNumber}`,
        conditions: [{ id: `${Date.now()}-1`, field: 'status', operator: 'is', value: '' }]
      }
    ]);
  };

  // Remove condition group
  const removeConditionGroup = (groupId: string) => {
    if (conditionGroups.length > 1) {
      setConditionGroups(conditionGroups.filter(g => g.id !== groupId));
    }
  };

  // Add condition to a group
  const addConditionToGroup = (groupId: string) => {
    setConditionGroups(conditionGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: [
            ...group.conditions,
            { id: `${groupId}-${Date.now()}`, field: 'status', operator: 'is', value: '' }
          ]
        };
      }
      return group;
    }));
  };

  // Remove condition from a group
  const removeConditionFromGroup = (groupId: string, conditionId: string) => {
    setConditionGroups(conditionGroups.map(group => {
      if (group.id === groupId && group.conditions.length > 1) {
        return {
          ...group,
          conditions: group.conditions.filter(c => c.id !== conditionId)
        };
      }
      return group;
    }));
  };

  // Update condition in a group
  const updateConditionInGroup = (groupId: string, conditionId: string, updates: Partial<Condition>) => {
    setConditionGroups(conditionGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          conditions: group.conditions.map(c => {
            if (c.id === conditionId) {
              const updated = { ...c, ...updates };
              // Reset value when field changes
              if (updates.field && updates.field !== c.field) {
                updated.value = '';
                const newFieldDef = fieldDefinitions.find(f => f.value === updates.field);
                if (newFieldDef && newFieldDef.operators.length > 0) {
                  updated.operator = newFieldDef.operators[0].value;
                }
              }
              return updated;
            }
            return c;
          })
        };
      }
      return group;
    }));
  };

  // Get field definition
  const getFieldDef = (fieldValue: string) => {
    return fieldDefinitions.find(f => f.value === fieldValue);
  };

  // Convert a single condition to filter format
  const conditionToFilter = (c: Condition): FilterCondition | null => {
    if (c.value === '' || c.value === null || c.value === undefined) return null;

    const fieldDef = getFieldDef(c.field);
    let operator = c.operator;
    let value = c.value;

    // Map operators to backend format
    if (operator === 'is') {
      operator = 'in';
      value = [c.value];
    } else if (operator === 'is_not') {
      operator = 'not_in';
      value = [c.value];
    } else if (operator === 'includes') {
      operator = 'in';
      value = [c.value];
    } else if (operator === 'not_includes') {
      operator = 'not_in';
      value = [c.value];
    } else if (operator === 'greater_than' && fieldDef?.valueType === 'relative_date') {
      operator = 'before';
      const days = parseInt(c.value) || 0;
      const date = new Date();
      date.setDate(date.getDate() - days);
      value = format(date, 'yyyy-MM-dd');
    } else if (operator === 'less_than' && fieldDef?.valueType === 'relative_date') {
      operator = 'after';
      const days = parseInt(c.value) || 0;
      const date = new Date();
      date.setDate(date.getDate() - days);
      value = format(date, 'yyyy-MM-dd');
    }

    // Special handling for assignee
    if (c.field === 'assignee_id' && c.value === 'unassigned') {
      return { field: 'assignee_id', operator: 'unassigned', value: null };
    }
    if (c.field === 'assignee_id' && c.value === 'me') {
      return { field: 'assignee_id', operator: 'me', value: null };
    }

    return { field: c.field, operator, value };
  };

  // Build filters from condition groups
  const buildFilters = (): { filters: FilterCondition[], groups?: any[], groupLogic?: string } => {
    // If only one group, use flat structure for backward compatibility
    if (conditionGroups.length === 1) {
      const filters = conditionGroups[0].conditions
        .map(conditionToFilter)
        .filter((f): f is FilterCondition => f !== null);
      return { filters };
    }

    // Multiple groups - use new structure
    const groups = conditionGroups.map(group => ({
      conditions: group.conditions
        .map(conditionToFilter)
        .filter((f): f is FilterCondition => f !== null)
    })).filter(g => g.conditions.length > 0);

    // Also flatten all filters for backward compatibility preview
    const allFilters: FilterCondition[] = [];
    conditionGroups.forEach(group => {
      group.conditions.forEach(c => {
        const filter = conditionToFilter(c);
        if (filter) allFilters.push(filter);
      });
    });

    return {
      filters: allFilters,
      groups,
      groupLogic: groupLogicType
    };
  };

  // Build query params for preview
  const buildPreviewParams = useMemo(() => {
    const filterResult = buildFilters();
    const filterConfig: CustomViewFilterConfig = { filters: filterResult.filters };
    const sortConfig = { sortBy, sortOrder };

    const queryString = customViewsService.buildQueryString(filterConfig, sortConfig);
    const urlParams = new URLSearchParams(queryString);
    const params: any = { page: 1, limit: 10 };

    urlParams.forEach((value, key) => {
      params[key] = value;
    });

    // Debug logging
    console.log('buildPreviewParams DEBUG:', {
      conditionGroups,
      filterResult,
      queryString,
      params
    });

    return params;
  }, [conditionGroups, sortBy, sortOrder, groupLogicType]);

  // Preview query
  const { data: previewData, isLoading: previewLoading, isFetching: previewFetching } = useQuery({
    queryKey: ['ticket-preview', buildPreviewParams],
    queryFn: () => ticketsService.getTickets(buildPreviewParams),
    enabled: showPreview && open,
    staleTime: 0,
  });

  // Reset form
  const resetForm = () => {
    setName('');
    setIconName('Mail');
    setSortBy('created_at');
    setSortOrder('desc');
    setShowPreview(true);
    setGroupLogicType('AND');
    setConditionGroups([{
      id: '1',
      name: 'Bedingungsgruppe 1',
      conditions: [{ id: '1-1', field: 'status', operator: 'is', value: '' }]
    }]);
  };

  // Handle save
  const handleSave = async () => {
    if (!name.trim()) return;

    const filterResult = buildFilters();
    const filterConfig: any = { filters: filterResult.filters };

    // Include groups and groupLogic if multiple groups
    if (filterResult.groups && filterResult.groups.length > 1) {
      filterConfig.groups = filterResult.groups;
      filterConfig.groupLogic = filterResult.groupLogic;
    }

    if (isEditMode && editView) {
      await updateMutation.mutateAsync({
        id: editView.id,
        data: {
          name: name.trim(),
          iconName,
          filterConfig,
          sortConfig: { sortBy, sortOrder }
        }
      });
    } else {
      await createMutation.mutateAsync({
        name: name.trim(),
        iconName,
        filterConfig,
        sortConfig: { sortBy, sortOrder }
      });
    }

    resetForm();
    onOpenChange(false);
    onSuccess?.();
  };

  // Format ticket number - tam format göster
  const formatTicketNumber = (ticketNumber: string) => {
    return ticketNumber || '';
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'open': return 'bg-amber-100 text-amber-700';
      case 'in_progress': return 'bg-purple-100 text-purple-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'solved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Render value input based on field type
  const renderValueInput = (condition: Condition, groupId: string) => {
    const fieldDef = getFieldDef(condition.field);
    if (!fieldDef) return null;

    const handleChange = (value: any) => updateConditionInGroup(groupId, condition.id, { value });

    switch (fieldDef.valueType) {
      case 'select':
        return (
          <Select value={condition.value} onValueChange={handleChange}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[150px] bg-white">
              <SelectValue placeholder={t('views:u.phSelectValue', 'Wert wählen...')} />
            </SelectTrigger>
            <SelectContent>
              {fieldDef.options?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.labelKey ? t(opt.labelKey, opt.label) : opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'agent':
        return (
          <Select value={condition.value} onValueChange={handleChange}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[150px] bg-white">
              <SelectValue placeholder={t('views:u.phSelectAgent', 'Agent wählen...')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">{t('ticket:ph.unassigned', 'Nicht zugewiesen')}</SelectItem>
              <SelectItem value="me">{t('views:f.sAssignedToMe', 'Mir zugewiesen')}</SelectItem>
              {agentsData?.data?.agents?.map((agent: any) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.firstName} {agent.lastName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'watcher':
        return (
          <Select value={condition.value} onValueChange={handleChange}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[150px] bg-white">
              <SelectValue placeholder={t('views:u.phSelectWatcher', 'Beobachter wählen...')} />
            </SelectTrigger>
            <SelectContent>
              {agentsData?.data?.agents?.map((agent: any) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.firstName} {agent.lastName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'tag':
        return (
          <Select value={condition.value} onValueChange={handleChange}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[150px] bg-white">
              <SelectValue placeholder={t('views:u.phSelectTag', 'Tag wählen...')} />
            </SelectTrigger>
            <SelectContent>
              {tagsData?.data?.tags?.map((tag: any) => (
                <SelectItem key={tag.id} value={tag.name}>
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tag.color }} />
                    {tag.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'organization':
        return (
          <Select value={condition.value} onValueChange={handleChange}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[150px] bg-white">
              <SelectValue placeholder={t('views:u.phSelectOrg', 'Organisation wählen...')} />
            </SelectTrigger>
            <SelectContent>
              {allowedOrganizations.map((org: any) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'relative_date':
        return (
          <div className="flex items-center gap-2 flex-1 min-w-[150px]">
            <Input
              type="number"
              value={condition.value}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={t('views:u.phCount', 'Anzahl')}
              className="h-8 text-xs w-20 bg-white"
            />
            <span className="text-xs text-slate-500">{t('views:u.dDays', 'Tagen')}</span>
          </div>
        );

      case 'text':
      default:
        return (
          <Input
            value={condition.value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={t('views:u.phEnterValue', 'Wert eingeben...')}
            className="h-8 text-xs flex-1 min-w-[150px] bg-white"
          />
        );
    }
  };

  const validConditionsCount = conditionGroups.reduce((total, group) =>
    total + group.conditions.filter(c => c.value !== '' && c.value !== null).length, 0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 overflow-hidden sm:max-w-[1200px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <DialogHeader className="px-5 py-4 border-b bg-slate-50/50">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#0F99FD]" />
              {isEditMode ? 'Ansicht bearbeiten' : 'Ansicht erstellen'}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex flex-1 min-h-0">
          {/* Left: Query Builder */}
          <div className="flex-1 border-r flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {/* Basic Info */}
              <div className="p-5 border-b bg-white">
                <div className="grid grid-cols-[1fr,140px] gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">{t('views:u.dViewName', 'Ansicht Name')}</label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('views:u.phEgUrgent', 'z.B. Dringende offene Tickets')}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">{t('views:u.dIcon', 'Icon')}</label>
                    <Select value={iconName} onValueChange={setIconName}>
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const Icon = iconMap[iconName];
                              return Icon ? <Icon className="h-3.5 w-3.5" /> : null;
                            })()}
                            <span>{(() => { const o = iconOptions.find(x => x.value === iconName); return o ? t(o.labelKey, o.label) : null; })()}</span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="h-3.5 w-3.5" />
                              <span className="text-xs">{opt.labelKey ? t(opt.labelKey, opt.label) : opt.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Conditions Builder */}
              <div className="p-5 bg-slate-50/30">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                    {t('views:u.dConditionsFor', 'Bedingungen für angezeigte Tickets')}
                  </label>
                  {conditionGroups.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500">{t('views:u.dGroupsLinked', 'Gruppen verknüpft mit:')}</span>
                      <Select value={groupLogicType} onValueChange={(v) => setGroupLogicType(v as 'AND' | 'OR')}>
                        <SelectTrigger className="h-7 w-[100px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">UND</SelectItem>
                          <SelectItem value="OR">{t('views:u.dOr', 'ODER')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {conditionGroups.map((group, groupIndex) => (
                    <div key={group.id}>
                      {/* Group connector */}
                      {groupIndex > 0 && (
                        <div className="flex items-center justify-center py-2">
                          <div className="h-px bg-slate-300 flex-1" />
                          <span className={cn(
                            "px-3 py-1 text-[10px] font-semibold rounded-full mx-2",
                            groupLogicType === 'AND'
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                          )}>
                            {groupLogicType === 'AND' ? 'UND' : 'ODER'}
                          </span>
                          <div className="h-px bg-slate-300 flex-1" />
                        </div>
                      )}

                      {/* Group container */}
                      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                        {/* Group header */}
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                          <span className="text-xs font-semibold text-slate-700">
                            {group.name}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500">
                              (Alle Bedingungen innerhalb = UND)
                            </span>
                            {conditionGroups.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeConditionGroup(group.id)}
                                className="h-6 w-6 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Conditions in group */}
                        <div className="p-3 space-y-2">
                          {group.conditions.map((condition, condIndex) => (
                            <div key={condition.id} className="flex items-center gap-2 bg-slate-50/50 p-2.5 rounded-md border border-slate-100">
                              <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />

                              {/* Field Selector */}
                              <Select value={condition.field} onValueChange={(v) => updateConditionInGroup(group.id, condition.id, { field: v })}>
                                <SelectTrigger className="h-8 text-xs w-[160px] bg-white border-slate-200">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {fieldDefinitions.map(field => (
                                    <SelectItem key={field.value} value={field.value}>
                                      {field.labelKey ? t(field.labelKey, field.label) : field.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {/* Operator Selector */}
                              <Select value={condition.operator} onValueChange={(v) => updateConditionInGroup(group.id, condition.id, { operator: v })}>
                                <SelectTrigger className="h-8 text-xs w-[140px] bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {getFieldDef(condition.field)?.operators.map(op => (
                                    <SelectItem key={op.value} value={op.value}>
                                      {op.labelKey ? t(op.labelKey, op.label) : op.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {/* Value Input */}
                              {renderValueInput(condition, group.id)}

                              {/* Action Buttons */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeConditionFromGroup(group.id, condition.id)}
                                  disabled={group.conditions.length === 1}
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => addConditionToGroup(group.id)}
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-green-500 hover:bg-green-50"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}

                          {/* Add condition to group button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addConditionToGroup(group.id)}
                            className="mt-1 h-7 text-[11px] text-slate-500 hover:text-slate-700"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {t('views:u.dAddCondition', 'Bedingung hinzufügen')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Group Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addConditionGroup}
                  disabled={conditionGroups.length >= MAX_GROUPS}
                  className="mt-4 h-8 text-xs border-dashed"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  {t('views:u.dAddGroup', 'Bedingungsgruppe hinzufügen')}
                  {conditionGroups.length >= MAX_GROUPS && (
                    <span className="ml-2 text-slate-400">(max. {MAX_GROUPS})</span>
                  )}
                </Button>
              </div>

              {/* Sort Options */}
              <div className="p-5 border-t bg-white">
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block mb-3">
                  {t('views:u.dSorting', 'Sortierung')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">{t('views:f.fCreatedDate', 'Erstellungsdatum')}</SelectItem>
                      <SelectItem value="updated_at">{t('views:f.fLastUpdate', 'Letzte Aktualisierung')}</SelectItem>
                      <SelectItem value="status">{t('common:field.status', 'Status')}</SelectItem>
                      <SelectItem value="priority">{t('ticketList:column.priority', 'Priorität')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">{t('views:u.dNewestFirst', 'Neueste zuerst')}</SelectItem>
                      <SelectItem value="asc">{t('views:u.dOldestFirst', 'Älteste zuerst')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Preview Panel */}
          <div className="w-[420px] flex flex-col bg-slate-50/30 min-h-0">
            <div className="px-4 py-3 border-b bg-white/80 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">{t('common:field.preview', 'Vorschau')}</span>
                  {previewFetching && (
                    <Loader2 className="h-3.5 w-3.5 text-[#0F99FD] animate-spin" />
                  )}
                </div>
                {!previewFetching && previewData?.data?.tickets && (
                  <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700">
                    {previewData.data.totalCount || previewData.data.tickets.length} Treffer gefunden
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              {previewLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-6 w-6 text-[#0F99FD] animate-spin" />
                </div>
              ) : previewData?.data?.tickets && previewData.data.tickets.length > 0 ? (
                <div className="divide-y">
                  {previewData.data.tickets.slice(0, 10).map((ticket: any) => (
                    <div key={ticket.id} className="px-4 py-3 hover:bg-white/50 transition">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[11px] font-mono text-[#0F99FD] font-semibold">
                          {formatTicketNumber(ticket.ticket_number)}
                        </span>
                        <span className={cn("text-[9px] px-1.5 py-0.5 rounded font-medium", getStatusColor(ticket.status))}>
                          {ticket.status}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-slate-800 truncate mb-1">
                        {ticket.subject}
                      </p>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span>{ticket.requester?.email || 'Unbekannt'}</span>
                        <span>•</span>
                        <span>{format(new Date(ticket.created_at), 'dd.MM.yy HH:mm', { locale: dfLocale() })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                  <Filter className="h-8 w-8 mb-2 opacity-30" />
                  <p className="text-xs font-medium">{t('views:u.dNoEntries', 'KEINE EINTRÄGE')}</p>
                  <p className="text-[10px] mt-1">{t('views:u.dAdjustConditions', 'Passen Sie die Bedingungen an')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            {validConditionsCount > 0 && (
              <Badge className="text-[10px] bg-[#0F99FD]/10 text-[#0F99FD] hover:bg-[#0F99FD]/20">
                {validConditionsCount} {validConditionsCount === 1 ? 'Bedingung' : 'Bedingungen'} aktiv
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-9 text-xs px-4">
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || createMutation.isPending || updateMutation.isPending}
              className="h-9 text-xs px-5 bg-[#0F99FD] hover:bg-[#0F99FD]/90"
            >
              <Save className="h-3.5 w-3.5 mr-2" />
              {createMutation.isPending || updateMutation.isPending
                ? (isEditMode ? 'Wird aktualisiert...' : 'Wird erstellt...')
                : (isEditMode ? 'Ansicht aktualisieren' : 'Ansicht erstellen')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
